import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useFeature } from '@treams/treams-hooks';
import { useIsSupport } from '@treams/treams-hooks';
import {
	AddIcon,
	ArchiveIcon,
	BackwardIcon,
	CalendarIcon,
	ChevronDownIcon,
	CloseIcon,
	DownloadIcon,
	JunkIcon,
	ListIcon,
	MailIcon,
	SendIcon,
} from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, ContextMenu } from '@treams/treams-ui';

import BulkArchiveUserModal from './BulkArchiveUserModal';
import BulkAssignCycleModal from './BulkAssignCycleModal';
import BulkExportDataModal from './BulkExportDataModal';
import BulkExportEmployeesModal from './BulkExportEmployeesModal';
import BulkObfuscateUserModal from './BulkObfuscateModal';
import BulkRemoveCycleModal from './BulkRemoveCycleModal';
import BulkResendActivationMailModal from './BulkResendActivationMailModal';
import BulkRestoreUserModal from './BulkRestoreUserModal';
import BulkSingleSignOnModal from './BulkSingleSignOnModal';
import BulkUpdateActivationDateModal from './BulkUpdateActivationDateModal';

function EmployeeBulkActions(props: { onAction: (action: string) => void; onBlur: () => void }) {
	const { t } = useTranslation();
	const isSupport = useIsSupport();
	const cyclesEnabled = useFeature(graphql.FeatureName.Cycles);

	return (
		<Box ref={(ref) => ref?.focus()} tabIndex={-1} onBlur={() => props.onBlur()}>
			<ContextMenu.Item
				icon={<CalendarIcon />}
				onClick={() => props.onAction('updateActivationDate')}
				title={t('employees.updateActivationDate.title')}
			/>

			<ContextMenu.Item
				icon={<SendIcon />}
				onClick={() => props.onAction('resendActivationMail')}
				title={t('employees.resendActivationMail.title')}
			/>

			<ContextMenu.Item
				icon={<MailIcon />}
				onClick={() => props.onAction('singleSignOn')}
				title={t('employees.toggleSingleSignOn.title')}
			/>

			<ContextMenu.Separator />

			{cyclesEnabled && (
				<>
					<ContextMenu.Item
						icon={<AddIcon />}
						onClick={() => props.onAction('assignCycle')}
						title={t('employees.assignCycle.title')}
					/>

					<ContextMenu.Item
						icon={<CloseIcon />}
						onClick={() => props.onAction('removeCycle')}
						title={t('employees.removeCycle.title')}
					/>

					<ContextMenu.Separator />
				</>
			)}

			<ContextMenu.Item
				icon={<DownloadIcon />}
				onClick={() => props.onAction('exportData')}
				title={t('employees.exportData.title')}
			/>
			<ContextMenu.Item
				icon={<ListIcon />}
				onClick={() => props.onAction('exportEmployees')}
				title={t('employees.exportEmployees.title')}
			/>

			<ContextMenu.Item
				icon={<BackwardIcon />}
				onClick={() => props.onAction('restore')}
				title={t('employees.restore.title')}
			/>

			<ContextMenu.Item
				icon={<ArchiveIcon />}
				onClick={() => props.onAction('archive')}
				title={t('employees.archive.title')}
				type="destructive"
			/>
			{isSupport && (
				<ContextMenu.Item
					icon={<JunkIcon />}
					onClick={() => props.onAction('obfuscate')}
					title={t('employees.bulkObfuscate.title')}
					type="destructive"
				/>
			)}
		</Box>
	);
}

const MODALS = {
	archive: BulkArchiveUserModal,
	assignCycle: BulkAssignCycleModal,
	removeCycle: BulkRemoveCycleModal,
	resendActivationMail: BulkResendActivationMailModal,
	restore: BulkRestoreUserModal,
	singleSignOn: BulkSingleSignOnModal,
	updateActivationDate: BulkUpdateActivationDateModal,
	exportData: BulkExportDataModal,
	exportEmployees: BulkExportEmployeesModal,
	obfuscate: BulkObfuscateUserModal,
};

function EmployeesBulkModalComponent(key: string) {
	const Component = MODALS[key];

	if (!Component) {
		throw new Error('Unknown bulk action.');
	}

	return Component;
}

function EmployeesBulkModal(props: {
	modal: string;
	users: { id: string; firstName: string; lastName: string }[];
	onDismiss: () => void;
}) {
	const Component = EmployeesBulkModalComponent(props.modal);
	return <Component users={props.users} onDismiss={props.onDismiss} />;
}

export default function EmployeesBulkButton(props: {
	selection: { id: string; firstName: string; lastName: string }[];
}) {
	const { t } = useTranslation();

	const [, setMenu] = useState(false);

	const [modal, setModal] = useState<string | undefined>();

	return (
		<>
			<ContextMenu.Target
				menu={
					<ContextMenu>
						<EmployeeBulkActions
							onAction={(action) => {
								setModal(action);
								setMenu(false);
							}}
							onBlur={() => setMenu(false)}
						/>
					</ContextMenu>
				}
			>
				<Button rightIcon={<ChevronDownIcon />} title={t('employees.bulkActions.title')} small />
			</ContextMenu.Target>
			{modal && (
				<EmployeesBulkModal
					modal={modal}
					users={props.selection}
					onDismiss={() => setModal(undefined)}
				/>
			)}
		</>
	);
}
